/**
 * resendService.ts
 *
 * ⚠️  RÈGLE DE SÉCURITÉ ABSOLUE :
 *     Ce service n'appelle JAMAIS l'API Resend directement depuis le navigateur.
 *     Tous les envois passent par l'Edge Function Supabase `send-email`.
 *     La clé API Resend est stockée uniquement dans les secrets Supabase.
 *
 * Architecture :
 *   Frontend → Edge Function `send-email` → API Resend → Destinataire
 */

import { EMAIL_MODE, RESEND_FROM_EMAIL, RESEND_REPLY_TO, SUPABASE_URL } from '@/lib/envConfig';
import { supabase } from '@/lib/supabaseClient';
import * as emailTemplates from '@/lib/emailTemplates';

// ============================================================================
// TYPES
// ============================================================================

export interface EmailPayload {
  to: string;
  subject: string;
  bodyText: string;
  bodyHtml?: string;
  replyTo?: string;
}

export interface EmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

// ============================================================================
// VALIDATION
// ============================================================================

export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// ============================================================================
// ENVOI
// ============================================================================

/**
 * Envoie un email transactionnel.
 *
 * Mode 'console' (développement) : affiche dans la console, aucun appel réseau.
 * Mode 'edge'    (production)    : appelle l'Edge Function Supabase `send-email`
 *                                  avec le token JWT de la session courante.
 */
export async function sendTransactionalEmail(payload: EmailPayload): Promise<EmailResult> {
  // ── Validation ──────────────────────────────────────────────────────────────
  if (!validateEmail(payload.to)) {
    return { success: false, error: `Adresse email invalide : "${payload.to}"` };
  }
  if (!payload.subject?.trim()) {
    return { success: false, error: 'Sujet manquant' };
  }
  if (!payload.bodyText?.trim()) {
    return { success: false, error: 'Corps du message manquant' };
  }

  // ── Mode console (développement) ────────────────────────────────────────────
  if (EMAIL_MODE === 'console') {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📧 [EMAIL — MODE CONSOLE]');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(`📬 FROM     : ${RESEND_FROM_EMAIL}`);
    console.log(`📬 TO       : ${payload.to}`);
    console.log(`📬 REPLY-TO : ${payload.replyTo ?? RESEND_REPLY_TO}`);
    console.log(`📋 SUBJECT  : ${payload.subject}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📄 BODY:');
    console.log(payload.bodyText);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n');
    return { success: true, messageId: `console-${Date.now()}` };
  }

  // ── Mode edge (production) ───────────────────────────────────────────────────
  if (!SUPABASE_URL) {
    return { success: false, error: 'SUPABASE_URL non configuré — impossible d\'appeler l\'Edge Function' };
  }

  // Récupérer le token JWT de la session Supabase courante
  // L'Edge Function est déployée avec verify_jwt=true → le token est obligatoire
  let accessToken: string | null = null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    accessToken = session?.access_token ?? null;
  } catch {
    // Pas bloquant : on tente quand même l'appel (l'EF retournera 401 si requis)
    console.warn('[resendService] Impossible de récupérer le token JWT');
  }

  const edgeFunctionUrl = `${SUPABASE_URL}/functions/v1/send-email`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  try {
    const res = await fetch(edgeFunctionUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        to: payload.to,
        subject: payload.subject,
        bodyText: payload.bodyText,
        bodyHtml: payload.bodyHtml,
        replyTo: payload.replyTo ?? RESEND_REPLY_TO,
      }),
    });

    let data: { success: boolean; messageId?: string; error?: string };
    try {
      data = await res.json();
    } catch {
      return { success: false, error: `Réponse non-JSON de l\'Edge Function (HTTP ${res.status})` };
    }

    if (!res.ok || !data.success) {
      const errMsg = data.error ?? `HTTP ${res.status}`;
      console.error('[resendService] Erreur Edge Function:', errMsg);
      return { success: false, error: errMsg };
    }

    return { success: true, messageId: data.messageId };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Erreur réseau inconnue';
    console.error('[resendService] Erreur fetch Edge Function:', message);
    return { success: false, error: message };
  }
}

// ============================================================================
// HELPERS — PRÉPARATION DES TEMPLATES
// ============================================================================

/**
 * Prépare un email de notification de changement de statut pour le candidat
 */
export function prepareStatusEmail(
  candidateEmail: string,
  role: 'AO' | 'COMMERCE',
  newStatus: 'FORM_SUBMITTED' | 'VALIDATED' | 'REJECTED',
  candidateName?: string | null
): EmailPayload | null {
  const name = candidateName || 'Candidat';
  const roleLabel = role === 'AO' ? 'Apporteur d\'affaires' : 'Collaborateur commercial';

  if (newStatus === 'FORM_SUBMITTED') {
    return {
      to: candidateEmail,
      subject: `TP13 — Candidature ${roleLabel} reçue`,
      bodyText: `Bonjour ${name},\n\nNous avons bien reçu votre candidature pour le poste de ${roleLabel}.\n\nNotre équipe va l'examiner dans les plus brefs délais. Vous recevrez une réponse sous 48 à 72 heures.\n\nÀ très bientôt,\nL'équipe TP13\n\n---\nTP13 — Réseau de distribution B2B\ncontact@tp13.fr`,
    };
  }

  if (newStatus === 'VALIDATED') {
    return {
      to: candidateEmail,
      subject: `TP13 — Candidature ${roleLabel} validée ✅`,
      bodyText: `Bonjour ${name},\n\nFélicitations ! Votre candidature pour le poste de ${roleLabel} a été validée.\n\nVous pouvez désormais accéder à votre espace personnel sur TP13.fr et commencer à utiliser nos services.\n\nConnectez-vous dès maintenant : https://tp13.fr/login\n\nBienvenue dans l'équipe TP13 !\n\nL'équipe TP13\n\n---\nTP13 — Réseau de distribution B2B\ncontact@tp13.fr`,
    };
  }

  if (newStatus === 'REJECTED') {
    return {
      to: candidateEmail,
      subject: `TP13 — Candidature ${roleLabel}`,
      bodyText: `Bonjour ${name},\n\nNous vous remercions pour l'intérêt que vous portez à TP13.\n\nAprès examen de votre candidature pour le poste de ${roleLabel}, nous ne pouvons malheureusement pas donner suite à votre demande pour le moment.\n\nNous vous encourageons à postuler à nouveau dans le futur si votre situation évolue.\n\nCordialement,\nL'équipe TP13\n\n---\nTP13 — Réseau de distribution B2B\ncontact@tp13.fr`,
    };
  }

  return null;
}

/**
 * Prépare un email de notification interne pour l'admin
 */
export function prepareInternalNotification(params: {
  candidateName: string;
  candidateEmail: string;
  role: 'AO' | 'COMMERCE';
  newStatus: 'FORM_SUBMITTED' | 'VALIDATED' | 'REJECTED';
}): EmailPayload {
  const { candidateName, candidateEmail, role, newStatus } = params;
  const roleLabel = role === 'AO' ? 'Apporteur d\'affaires' : 'Collaborateur commercial';
  
  const statusLabels: Record<string, string> = {
    FORM_SUBMITTED: 'Formulaire envoyé',
    VALIDATED: 'Validé',
    REJECTED: 'Rejeté',
  };

  return {
    to: 'karlcibois@gmail.com',
    subject: `[TP13 Admin] ${roleLabel} — ${statusLabels[newStatus]}`,
    bodyText: `Notification admin TP13\n\n` +
      `Candidat : ${candidateName}\n` +
      `Email : ${candidateEmail}\n` +
      `Rôle : ${roleLabel}\n` +
      `Nouveau statut : ${statusLabels[newStatus]}\n\n` +
      `Accéder à la console admin : https://tp13.fr/admin/qa\n\n` +
      `---\nNotification automatique TP13`,
  };
}

/**
 * Prépare un email pour l'AO quand le statut d'une société recommandée change
 */
export function prepareSocieteStatusEmail(params: {
  aoEmail: string;
  aoName: string;
  societeName: string;
  oldStatus: string;
  newStatus: string;
}): EmailPayload {
  const template = emailTemplates.societeStatusChanged({
    aoName: params.aoName,
    societeName: params.societeName,
    oldStatus: params.oldStatus,
    newStatus: params.newStatus,
  });

  return {
    to: params.aoEmail,
    subject: template.subject,
    bodyText: template.bodyText,
  };
}

/**
 * Prépare un email pour l'admin quand une nouvelle inscription est créée
 */
export function prepareNewSignupAdminEmail(params: {
  userName: string;
  userEmail: string;
  userPhone?: string | null;
  userCity?: string | null;
  role: 'AO' | 'COMMERCE';
}): EmailPayload {
  const template = emailTemplates.newSignupAdmin(params);

  return {
    to: 'karlcibois@gmail.com',
    subject: template.subject,
    bodyText: template.bodyText,
  };
}

/**
 * Prépare un email pour l'admin quand une nouvelle candidature est soumise
 */
export function prepareNewCandidatureAdminEmail(params: {
  candidateName: string;
  candidateEmail: string;
  candidatePhone?: string | null;
  candidateCity?: string | null;
  role: 'AO' | 'COMMERCE';
}): EmailPayload {
  const template = emailTemplates.newCandidatureAdmin(params);

  return {
    to: 'karlcibois@gmail.com',
    subject: template.subject,
    bodyText: template.bodyText,
  };
}

/**
 * Prépare un email pour l'admin quand un nouveau ticket est créé
 */
export function prepareNewTicketAdminEmail(params: {
  ticketId: number;
  userName: string;
  userEmail: string;
  userPhone?: string | null;
  userCity?: string | null;
  role?: 'AO' | 'COMMERCE' | null;
  category: string;
  priority: string;
  subject: string;
  message: string;
}): EmailPayload {
  const template = emailTemplates.newTicketAdmin(params);

  return {
    to: 'karlcibois@gmail.com',
    subject: template.subject,
    bodyText: template.bodyText,
  };
}

/**
 * Prépare un email pour l'admin quand un nouveau message est ajouté à un ticket
 */
export function prepareNewTicketMessageAdminEmail(params: {
  ticketId: number;
  userName: string;
  userEmail: string;
  userPhone?: string | null;
  userCity?: string | null;
  role?: 'AO' | 'COMMERCE' | null;
  subject: string;
  message: string;
}): EmailPayload {
  const template = emailTemplates.newTicketMessageAdmin(params);

  return {
    to: 'karlcibois@gmail.com',
    subject: template.subject,
    bodyText: template.bodyText,
  };
}

/**
 * Prépare un email pour l'admin quand un formulaire externe est reçu
 */
export function prepareNewExternalFormAdminEmail(params: {
  formType: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  userCity?: string;
  additionalData?: Record<string, unknown>;
}): EmailPayload {
  const template = emailTemplates.newExternalFormAdmin(params);

  return {
    to: 'karlcibois@gmail.com',
    subject: template.subject,
    bodyText: template.bodyText,
  };
}